import { NgClass } from '@angular/common';
import { Component, input, isSignal, output, Signal, WritableSignal } from '@angular/core';

import { AuxIconName } from '@shared/components/icon/icon';
import { IconComponent } from '@shared/components/icon/icon.component';
import { TooltipDirective } from '@shared/directives/tooltip.directive';

export enum SuggestionIconColor {
  BLUE = '#045a9e',
  GREEN = '#24a148',
  LIGHT_BLUE = '#009de2',
  PURPLE = '#5359fa',
}

export interface SuggestionData {
  action?: () => Promise<void> | void;
  disabled?: boolean | Signal<boolean>;
  hidden?: boolean | Signal<boolean>;
  iconColor?: SuggestionIconColor;
  iconName?: string;
  name: string;
  pendoTag?: string;
  tooltip?: Signal<string> | string;
}

@Component({
  imports: [IconComponent, NgClass, TooltipDirective],
  selector: 'aux-suggestion',
  template: `
    <div
      class="flex h-[48px] items-center rounded-[8px] border border-aux-gray-dark p-[12px] shadow-md"
      [ngClass]="{
        'hover:border-aux-blue': !itemDisabled,
        'hover:bg-aux-blue-lightHover': !itemDisabled,
        'hover:shadow-lg': !itemDisabled,
        'cursor-pointer': !itemDisabled,
        'opacity-70': itemDisabled,
        'cursor-not-allowed': itemDisabled,
      }"
      [auxTooltip]="itemTooltip"
      [attr.data-pendo-id]="data()?.pendoTag"
      (click)="!itemDisabled && action.emit()"
    >
      @if (data()?.iconName) {
        <aux-icon [name]="iconName" class="mr-2" [style.color]="data()?.iconColor || 'unset'" />
      }
      <div class="font-semibold">{{ data()?.name }}</div>
    </div>
  `,
})
export class SuggestionComponent {
  data = input<SuggestionData>();

  action = output();

  get iconColor(): SuggestionIconColor {
    return this.data()?.iconColor || SuggestionIconColor.BLUE;
  }

  get iconName(): AuxIconName {
    return this.data()?.iconName as AuxIconName;
  }

  get itemDisabled(): boolean {
    if (isSignal(this.data()?.disabled)) {
      return (this.data()?.disabled as WritableSignal<boolean>)();
    }

    return !!this.data()?.disabled;
  }

  get itemTooltip(): string {
    if (isSignal(this.data()?.tooltip)) {
      return (this.data()?.tooltip as WritableSignal<string>)();
    }

    return (this.data()?.tooltip as string) || '';
  }
}
